<template>
  <div v-if="views.length" class="shared-view-previews">
    <span class="shared-view-previews-label">Sample Shared Views</span>
    <ul class="shared-view-previews-list">
      <li v-for="view in views" :key="view.id">
        <router-link :to="`/view/${view.id}`">
          <span class="shared-view-previews-copy">
            <span class="shared-view-previews-question">&ldquo;{{ view.question }}&rdquo;</span>
            <span class="shared-view-previews-meta">{{ sourceLabel(view.source_count) }} · {{ dateLabel(view.created_at) }}</span>
          </span>
          <ArrowUpRightIcon class="shared-view-previews-arrow" />
        </router-link>
      </li>
    </ul>
    <router-link class="shared-view-previews-explore" to="/views#public-views-collection">
      <span>Explore more</span>
      <span class="shared-view-previews-explore-arrow" aria-hidden="true">&rarr;</span>
    </router-link>
  </div>
</template>

<script>
import sampleViews from '../../data/projectViewSamples.json';
import ArrowUpRightIcon from '../ui/ArrowUpRightIcon.vue';

export default {
  name: 'SharedViewPreviews',
  components: { ArrowUpRightIcon },
  data() {
    return {
      views: sampleViews,
    };
  },
  methods: {
    sourceLabel(count) {
      return `${count} original ${count === 1 ? 'piece' : 'pieces'}`;
    },
    dateLabel(timestamp) {
      if (!timestamp) return 'Date unavailable';
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        .format(new Date(timestamp));
    },
  },
};
</script>

<style scoped>
.shared-view-previews {
  margin: 1.6rem 0 2.4rem;
}

.shared-view-previews-label {
  display: block;
  margin-bottom: 0.7rem;
  color: var(--site-accent-muted);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.shared-view-previews-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.shared-view-previews-list li + li {
  margin-top: 0.5rem;
}

.shared-view-previews-list a {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.68rem 0.7rem;
  border-radius: 8px;
  box-shadow: inset 0 -1px rgba(0, 0, 0, 0.09);
  color: inherit;
  text-decoration: none;
  transition: background-color 220ms cubic-bezier(0.22, 1, 0.36, 1), color 160ms ease;
}

/* A quiet left affordance line, materialised from the entry's own edge so the
   hover reads as the source revealing a path rather than a flat fill. */
.shared-view-previews-list a::before {
  content: "";
  position: absolute;
  left: 0.1rem;
  top: 22%;
  bottom: 22%;
  width: 2px;
  border-radius: 2px;
  background: currentColor;
  opacity: 0;
  transform: scaleY(0.4);
  transform-origin: center;
  transition: opacity 200ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shared-view-previews-copy {
  display: grid;
  gap: 0.18rem;
  min-width: 0;
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shared-view-previews-question {
  font-size: 0.95rem;
  line-height: 1.4;
}

.shared-view-previews-meta {
  color: #767676;
  font: 0.72rem/1.3 "Quattrocento Sans", sans-serif;
}

.shared-view-previews-arrow {
  display: inline-block;
  flex: 0 0 auto;
  color: var(--site-accent);
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1), color 160ms ease;
}

.shared-view-previews-list a:hover,
.shared-view-previews-list a:focus-visible {
  background: rgba(0, 0, 0, 0.045);
  outline: none;
}

.shared-view-previews-list a:hover::before,
.shared-view-previews-list a:focus-visible::before {
  opacity: 0.55;
  transform: scaleY(1);
}

.shared-view-previews-list a:hover .shared-view-previews-question,
.shared-view-previews-list a:focus-visible .shared-view-previews-question {
  color: #242321;
}

.shared-view-previews-list a:hover .shared-view-previews-copy,
.shared-view-previews-list a:focus-visible .shared-view-previews-copy {
  transform: translateX(3px);
}

.shared-view-previews-list a:hover .shared-view-previews-arrow,
.shared-view-previews-list a:focus-visible .shared-view-previews-arrow {
  color: var(--site-accent-strong);
  transform: translate(3px, -2px);
}

.shared-view-previews-list a:active {
  transform: scale(0.985);
}

/* Apple-style explore entry: instant press feedback, gentle hover nudge,
   and the arrow following the eye so the affordance reads before the click. */
.shared-view-previews-explore {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-top: 0.9rem;
  padding: 0.68rem 0.7rem;
  border-radius: 8px;
  color: #555;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 160ms ease, color 160ms ease, transform 100ms ease-out;
}

.shared-view-previews-explore-arrow {
  font-size: 1.05rem;
  line-height: 1;
  color: var(--site-accent);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shared-view-previews-explore:hover,
.shared-view-previews-explore:focus-visible {
  background: rgba(0, 0, 0, 0.06);
  color: #242321;
  outline: none;
}

.shared-view-previews-explore:hover .shared-view-previews-explore-arrow,
.shared-view-previews-explore:focus-visible .shared-view-previews-explore-arrow {
  transform: translateX(3px);
}

.shared-view-previews-explore:active {
  transform: scale(0.97);
}

body.dark-mode .shared-view-previews-label {
  color: #a1a1a1;
}

body.dark-mode .shared-view-previews-list a {
  box-shadow: inset 0 -1px rgba(255, 255, 255, 0.09);
}

body.dark-mode .shared-view-previews-list a:hover,
body.dark-mode .shared-view-previews-list a:focus-visible {
  background: rgba(255, 255, 255, 0.09);
}

body.dark-mode .shared-view-previews-meta {
  color: #a1a1a1;
}

body.dark-mode .shared-view-previews-arrow {
  color: var(--site-accent);
}

body.dark-mode .shared-view-previews-list a:hover .shared-view-previews-question,
body.dark-mode .shared-view-previews-list a:focus-visible .shared-view-previews-question {
  color: #fff;
}

body.dark-mode .shared-view-previews-explore {
  color: #d5d5d5;
}

body.dark-mode .shared-view-previews-explore:hover,
body.dark-mode .shared-view-previews-explore:focus-visible {
  background: rgba(255, 255, 255, 0.09);
  color: #fff;
}

@media (prefers-reduced-motion: reduce) {
  .shared-view-previews-list a,
  .shared-view-previews-arrow,
  .shared-view-previews-explore-arrow {
    transition-duration: 0.01ms;
  }
}
</style>
